import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import apiService from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Client,
  ClientLocationDto,
  CreateWorkOrderDto,
  WorkOrderType,
  WorkOrderVendor,
} from '../../types';

interface ChangeableWorkOrder {
  clientId?: number;
  clientLocationId?: number;
  vendors?: WorkOrderVendor[];
  assignedUserId?: string;
  priorityId: number;
  statusId: number;
  categoryId?: number;
  type?: number;
  dueDate?: string;
  templateId?: number;
  externalWorkOrderNumber?: string;
  description?: string;
  signedOff: boolean;
  invoiced: boolean;
  nteAmount?: number | null;
  incurredCost?: number | null;
  incurredCostNote?: string | null;
  proposedCost?: number | null;
  actualCost?: number | null;
  quoteRequested: boolean;
  pricingReceived?: boolean;
  photosReceived?: boolean;
  invoiceReceived?: boolean;
  vendorPaid?: boolean;
  vendorPaidDate?: string | null;
  vendorPaymentReference?: string | null;
}

interface ChangeWorkOrderClientModalProps {
  isOpen: boolean;
  workOrderId: string;
  workOrder: ChangeableWorkOrder;
  onClose: () => void;
}

const getApiErrorMessage = (error: unknown): string => {
  const responseData = (error as { response?: { data?: { message?: string; errors?: string[] } } })?.response?.data;
  if (responseData?.message) {
    return responseData.message;
  }
  if (responseData?.errors?.length) {
    return responseData.errors.join(' ');
  }
  return 'Failed to change client';
};

const ChangeWorkOrderClientModal: React.FC<ChangeWorkOrderClientModalProps> = ({
  isOpen,
  workOrderId,
  workOrder,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>(undefined);
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  useEffect(() => {
    if (isOpen) {
      return;
    }
    setSearchTerm('');
    setCurrentPage(1);
    setSelectedClient(null);
    setSelectedLocationId(undefined);
  }, [isOpen]);

  const {
    data: clientsResponse,
    isLoading: clientsLoading,
    isError: clientsError,
  } = useQuery({
    queryKey: ['clients', 'change-work-order-client', currentPage, debouncedSearchTerm],
    queryFn: () => apiService.getClients(currentPage, 8, debouncedSearchTerm || undefined, 'name', 'asc'),
    enabled: isOpen,
  });

  const {
    data: locationsResponse,
    isLoading: locationsLoading,
  } = useQuery({
    queryKey: ['client-eligible-locations', selectedClient?.id, 'change-client'],
    queryFn: () => apiService.getEligibleClientLocations(selectedClient!.id),
    enabled: isOpen && !!selectedClient,
  });

  const clients = clientsResponse?.data?.data || [];
  const totalPages = clientsResponse?.data?.totalPages || 1;
  const locations: ClientLocationDto[] = locationsResponse?.data || [];
  const isCurrentClient = selectedClient?.id === workOrder.clientId;
  const isCurrentLocation = selectedLocationId === workOrder.clientLocationId;
  const hasChange = !!selectedClient && !!selectedLocationId && (!isCurrentClient || !isCurrentLocation);

  useEffect(() => {
    if (!selectedClient) {
      setSelectedLocationId(undefined);
      return;
    }

    const list = locationsResponse?.data ?? [];
    if (list.length === 0) {
      if (!locationsLoading) {
        setSelectedLocationId(undefined);
      }
      return;
    }

    setSelectedLocationId((current) => {
      if (current && list.some((location) => location.id === current)) {
        return current;
      }
      if (
        selectedClient.id === workOrder.clientId
        && workOrder.clientLocationId
        && list.some((location) => location.id === workOrder.clientLocationId)
      ) {
        return workOrder.clientLocationId;
      }
      const primary = list.find((location) => location.isPrimary) ?? list[0];
      return primary.id;
    });
  }, [selectedClient, locationsResponse, locationsLoading, workOrder.clientId, workOrder.clientLocationId]);

  const changeClientMutation = useMutation({
    mutationFn: () => {
      if (!selectedClient || !selectedLocationId) {
        throw new Error('Select a client and location');
      }

      const updateData: CreateWorkOrderDto = {
        clientId: selectedClient.id,
        clientLocationId: selectedLocationId,
        vendors: (workOrder.vendors ?? []).map((vendor) => ({
          vendorId: vendor.vendorId,
          isPrimary: vendor.isPrimary,
          status: vendor.status,
          notes: vendor.notes,
        })),
        assignedUserId: workOrder.assignedUserId || undefined,
        priorityId: workOrder.priorityId,
        statusId: workOrder.statusId,
        categoryId: workOrder.categoryId,
        equipmentId: undefined,
        type: (workOrder.type ?? WorkOrderType.Regular) as WorkOrderType,
        dueDate: workOrder.dueDate,
        templateId: workOrder.templateId,
        externalWorkOrderNumber: workOrder.externalWorkOrderNumber,
        description: workOrder.description,
        signedOff: workOrder.signedOff,
        invoiced: workOrder.invoiced,
        nteAmount: workOrder.nteAmount ?? undefined,
        incurredCost: workOrder.incurredCost ?? null,
        incurredCostNote: workOrder.incurredCostNote ?? null,
        proposedCost: workOrder.proposedCost ?? null,
        actualCost: workOrder.actualCost ?? null,
        quoteRequested: workOrder.quoteRequested,
        pricingReceived: workOrder.pricingReceived ?? false,
        photosReceived: workOrder.photosReceived ?? false,
        invoiceReceived: workOrder.invoiceReceived ?? false,
        vendorPaid: workOrder.vendorPaid ?? false,
        vendorPaidDate: workOrder.vendorPaidDate ?? undefined,
        vendorPaymentReference: workOrder.vendorPaymentReference ?? undefined,
      };

      return apiService.updateWorkOrder(parseInt(workOrderId, 10), updateData);
    },
    onSuccess: (result) => {
      if (!result?.success) {
        toast.error(result?.message || 'Failed to change client');
        return;
      }

      toast.success(selectedClient?.id === workOrder.clientId ? 'Location updated' : 'Client updated');
      queryClient.invalidateQueries({ queryKey: ['workorder', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['workOrder', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['workorders'] });
      queryClient.invalidateQueries({ queryKey: ['client-eligible-locations'] });
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setSelectedLocationId(undefined);
  };

  const handleLocationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLocationId(event.target.value ? parseInt(event.target.value, 10) : undefined);
  };

  const handleSave = () => {
    if (!hasChange || locationsLoading || changeClientMutation.isPending) {
      return;
    }
    changeClientMutation.mutate();
  };

  const canSave = hasChange && !locationsLoading && !changeClientMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Client" maxWidth="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Choose the client and location for this work order. You can keep the same client and pick a different location.
          Changing the client is blocked once the work order has an invoice.
        </p>

        <div>
          <label htmlFor="change-client-search" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Search clients
          </label>
          <div className="relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="change-client-search"
              type="search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Name, phone, or email"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
            />
          </div>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-56 overflow-y-auto" aria-busy={clientsLoading}>
          {clientsLoading && (
            <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Loading clients...</p>
          )}
          {clientsError && (
            <p className="px-3 py-4 text-sm text-red-600">Could not load clients.</p>
          )}
          {!clientsLoading && !clientsError && clients.length === 0 && (
            <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">No clients match that search.</p>
          )}
          {!clientsLoading && !clientsError && clients.map((client) => (
            <button
              key={client.id}
              type="button"
              onClick={() => handleSelectClient(client)}
              aria-pressed={selectedClient?.id === client.id}
              className={clsx(
                'w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0',
                selectedClient?.id === client.id
                  ? 'bg-accent-color-50 dark:bg-gray-700'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              )}
            >
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{client.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {[client.city, client.state].filter(Boolean).join(', ') || client.phone || client.email || 'No address on file'}
              </div>
            </button>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-gray-500 dark:text-gray-400">Page {currentPage} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {selectedClient && (
          <div>
            <label htmlFor="change-client-location" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Location for {selectedClient.name}
            </label>
            <select
              id="change-client-location"
              value={selectedLocationId ?? ''}
              onChange={handleLocationChange}
              disabled={locationsLoading || locations.length === 0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
            >
              <option value="">{locationsLoading ? 'Loading locations...' : 'Select a location'}</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.isPrimary ? 'Primary - ' : ''}{location.locationName}
                  {location.city ? ` (${location.city}${location.state ? `, ${location.state}` : ''})` : ''}
                </option>
              ))}
            </select>
            {!locationsLoading && locations.length === 0 && (
              <p className="mt-1 text-xs text-red-600">This client has no locations that can receive work orders.</p>
            )}
            {isCurrentClient && isCurrentLocation && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Pick a different location, or choose another client.</p>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            aria-label="Save client or location change"
            className="px-4 py-2 text-sm font-medium text-white bg-accent-color rounded-md hover:bg-accent-color-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {changeClientMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeWorkOrderClientModal;
